import { useCallback, useEffect, useState } from "react";
import type { CoachTemplateView } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  discardCoachTemplateDraft,
  fetchCoachTemplate,
  publishCoachTemplate,
  rollbackCoachTemplate,
  saveCoachTemplateDraft,
} from "@/lib/coach-templates-api";
import {
  coachTemplateEditorState,
  coachTemplateErrorText,
  savedText,
} from "@/lib/coach-template-editor";

// TES-1355 — "Coachmallar": the welcome template the coach's welcome draft
// is built from. The admin edits a draft, previews it with sample names and
// publishes it; publishing only changes drafts opened afterwards. Earlier
// versions can be loaded back into the draft. The template text itself is
// Emelie's (A-01); the labels on this page are ours.

const FIELD =
  "w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
const PRIMARY =
  "px-md py-sm rounded-md bg-brand text-white text-sm disabled:opacity-50";
const SECONDARY =
  "px-md py-sm rounded-md border border-surfaceMuted text-sm text-textSecondary disabled:opacity-50";

function when(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function CoachTemplatesRoute() {
  const { signOut } = useAuth();
  const [view, setView] = useState<CoachTemplateView | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) return void signOut();
      setError(coachTemplateErrorText(e instanceof Error ? e.message : ""));
    },
    [signOut],
  );

  const show = (next: CoachTemplateView) => {
    setView(next);
    setText(savedText(next));
  };

  useEffect(() => {
    fetchCoachTemplate("welcome")
      .then((next) => {
        setView(next);
        setText(savedText(next));
      })
      .catch(handleError);
  }, [handleError]);

  async function run(fn: () => Promise<CoachTemplateView>) {
    setBusy(true);
    setError(null);
    try {
      show(await fn());
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  const state = view ? coachTemplateEditorState(view, text) : null;

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Coachmallar</h1>

      {error ? (
        <div
          role="alert"
          className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm"
        >
          {error}
        </div>
      ) : null}

      {!view && !error ? (
        <p className="text-sm text-textSecondary">Hämtar mallen…</p>
      ) : null}

      {view && state ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-sm">
            <h2 className="text-lg font-medium">{view.title}</h2>
            <p className="text-xs text-textSecondary">
              {view.status === "published"
                ? `Publicerad version ${view.version} · ${when(view.updatedAt)}`
                : "Inte publicerad"}
              {view.draft
                ? ` · Opublicerat utkast sparat ${when(view.draft.updatedAt)}`
                : ""}
            </p>
          </div>
          <p className="text-sm text-textSecondary">
            Coachen får mallen som ett redigerbart utkast när hon skriver
            välkomstmeddelandet. Tillåtna platshållare:{" "}
            {view.placeholders.join(" och ")}.
          </p>

          {/* TES-1402 (Ö-590) — "Mallredigering skiljer tydligt sparat
              utkast från aktiv publicerad mall": with a draft saved, the
              live text is shown on its own, and the field says it edits the
              draft. */}
          {view.draft && view.status === "published" ? (
            <div className="flex flex-col gap-xs">
              <h3 className="text-sm font-medium">
                Aktiv publicerad mall (version {view.version})
              </h3>
              <p className="text-xs text-textSecondary">
                Det här är texten coacherna får i dag. Den ändras först när
                utkastet publiceras.
              </p>
              <div
                className="whitespace-pre-wrap rounded-md border border-surfaceMuted p-sm text-sm text-textSecondary"
                data-testid="coach-template-live"
              >
                {view.body}
              </div>
            </div>
          ) : null}

          <label className="flex flex-col gap-xs text-sm">
            {view.draft
              ? "Utkast – inte publicerat"
              : view.status === "published"
                ? "Mall – ändringar sparas som ett utkast"
                : "Mall – inte publicerad"}
            <textarea
              className={FIELD}
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-invalid={state.unknown.length > 0}
              aria-describedby="mall-varning"
            />
          </label>
          {state.unknown.length > 0 ? (
            <p id="mall-varning" className="text-sm text-danger">
              Okänd platshållare: {state.unknown.join(", ")}. Tillåtna är{" "}
              {view.placeholders.join(" och ")}.
            </p>
          ) : null}
          {state.missing.length > 0 ? (
            <p
              className="text-sm text-danger"
              data-testid="coach-template-missing"
            >
              Kan inte publiceras förrän mallen innehåller{" "}
              {state.missing.join(", ")}.
            </p>
          ) : null}

          <div className="flex flex-col gap-xs">
            <h3 className="text-sm font-medium">
              Förhandsvisning med exempelnamn
            </h3>
            <div className="whitespace-pre-wrap rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm">
              {state.preview}
            </div>
          </div>

          <div className="flex flex-wrap gap-sm">
            <button
              type="button"
              disabled={busy || !state.canSave}
              onClick={() => run(() => saveCoachTemplateDraft("welcome", text))}
              className={PRIMARY}
            >
              Spara utkast
            </button>
            <button
              type="button"
              disabled={busy || !state.canPublish}
              onClick={() => run(() => publishCoachTemplate("welcome"))}
              className={PRIMARY}
            >
              Publicera
            </button>
            {view.draft ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => discardCoachTemplateDraft("welcome"))}
                className={SECONDARY}
              >
                Släng utkastet
              </button>
            ) : null}
            {state.dirty ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setText(savedText(view))}
                className={SECONDARY}
              >
                Ångra ändringarna
              </button>
            ) : null}
          </div>

          {view.revisions.length > 0 ? (
            <div className="flex flex-col gap-xs">
              <h3 className="text-sm font-medium">Versionshistorik</h3>
              <ul className="flex flex-col gap-xs">
                {view.revisions.map((r) => (
                  <li
                    key={r.version}
                    className="flex flex-wrap items-center gap-sm text-sm"
                  >
                    <span>
                      Version {r.version} · {when(r.changedAt)}
                    </span>
                    {r.version !== view.version ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          run(() => rollbackCoachTemplate("welcome", r.version))
                        }
                        className="text-brand underline disabled:opacity-50"
                      >
                        Hämta till utkastet
                      </button>
                    ) : (
                      <span className="text-textSecondary">(publicerad)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
